/**
 * Enterprise System Card Component
 *
 * Displays an enterprise system in a card format with:
 * - System name, vendor, category
 * - Quality score badge
 * - Key statistics (modules, tables, APIs)
 * - Status and last update
 * - Quick actions
 */

import React from 'react';
import { Link } from 'react-router-dom';
import {
  Database,
  Layers,
  Code,
  FileText,
  TrendingUp,
  Calendar,
  ChevronRight,
} from 'lucide-react';
import type { EnterpriseSystem } from '../../lib/api/enterpriseSystemsApi';

interface EnterpriseSystemCardProps {
  system: EnterpriseSystem;
  onClick?: (system: EnterpriseSystem) => void;
}

const EnterpriseSystemCard: React.FC<EnterpriseSystemCardProps> = ({
  system,
  onClick,
}) => {
  /**
   * Get quality score color based on grade
   */
  const getQualityColor = (score: number): string => {
    if (score >= 0.9) return 'text-green-600 bg-green-50';
    if (score >= 0.8) return 'text-blue-600 bg-blue-50';
    if (score >= 0.7) return 'text-yellow-600 bg-yellow-50';
    if (score >= 0.6) return 'text-orange-600 bg-orange-50';
    return 'text-red-600 bg-red-50';
  };

  /**
   * Get quality grade letter
   */
  const getQualityGrade = (score: number): string => {
    if (score >= 0.9) return 'A';
    if (score >= 0.8) return 'B';
    if (score >= 0.7) return 'C';
    if (score >= 0.6) return 'D';
    return 'F';
  };

  /**
   * Get status badge color
   */
  const getStatusColor = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'published':
        return 'bg-green-100 text-green-800';
      case 'quality_review':
        return 'bg-blue-100 text-blue-800';
      case 'researching':
        return 'bg-yellow-100 text-yellow-800';
      case 'pending':
        return 'bg-gray-100 text-gray-800';
      case 'archived':
        return 'bg-gray-100 text-gray-500';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  /**
   * Format date to relative time
   */
  const formatRelativeTime = (dateString?: string): string => {
    if (!dateString) return 'Never';

    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  };

  const handleCardClick = () => {
    if (onClick) {
      onClick(system);
    }
  };

  return (
    <div
      className="group relative bg-white border border-neutral-200 rounded-lg hover:shadow-lg transition-all duration-200 overflow-hidden cursor-pointer"
      onClick={handleCardClick}
    >
      {/* Header */}
      <div className="p-6 border-b border-neutral-100">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-neutral-900 truncate group-hover:text-primary-600 transition-colors">
              {system.system_name}
            </h3>
            <p className="text-sm text-neutral-600 mt-1">
              {system.vendor} • {system.category}
              {system.version && ` • v${system.version}`}
            </p>
          </div>

          {/* Quality Score Badge */}
          <div
            className={`flex-shrink-0 ml-4 px-3 py-1 rounded-full text-sm font-semibold ${getQualityColor(
              system.quality_score
            )}`}
          >
            {getQualityGrade(system.quality_score)} •{' '}
            {Math.round(system.quality_score * 100)}%
          </div>
        </div>

        {/* Status Badge */}
        <div className="mt-3 flex items-center gap-2">
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
              system.status
            )}`}
          >
            {system.status.replace('_', ' ').toUpperCase()}
          </span>
          {system.current_phase && (
            <span className="text-xs text-neutral-500">
              Phase: {system.current_phase.replace('_', ' ')}
            </span>
          )}
        </div>
      </div>

      {/* Statistics Grid */}
      <div className="p-6 grid grid-cols-3 gap-4">
        {/* Modules */}
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center">
            <Layers className="w-5 h-5 text-primary-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-2xl font-bold text-neutral-900">
              {system.total_modules}
            </p>
            <p className="text-xs text-neutral-600 truncate">Modules</p>
          </div>
        </div>

        {/* Tables */}
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-10 h-10 bg-secondary-50 rounded-lg flex items-center justify-center">
            <Database className="w-5 h-5 text-secondary-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-2xl font-bold text-neutral-900">
              {system.total_tables}
            </p>
            <p className="text-xs text-neutral-600 truncate">Tables</p>
          </div>
        </div>

        {/* APIs */}
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
            <Code className="w-5 h-5 text-blue-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-2xl font-bold text-neutral-900">
              {system.total_apis}
            </p>
            <p className="text-xs text-neutral-600 truncate">APIs</p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="px-6 py-4 bg-neutral-50 border-t border-neutral-100 flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs text-neutral-600">
          <Calendar className="w-4 h-4" />
          <span>
            Updated {formatRelativeTime(system.updated_at)}
          </span>
        </div>

        <Link
          to={`/enterprise-systems/${system.system_id}`}
          className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
          onClick={(e) => e.stopPropagation()}
        >
          View Details
          <ChevronRight className="w-4 h-4" />
        </Link>
      </div>

      {/* Hover Effect Overlay */}
      <div className="absolute inset-0 border-2 border-transparent group-hover:border-primary-300 rounded-lg pointer-events-none transition-all" />
    </div>
  );
};

export default EnterpriseSystemCard;
